import i18n from "@/i18n";
/**
 * EmployeeMobileAccessDialog — Prompt 15B §8.
 *
 * Manager-only dialog that issues a one-time Employee Mobile
 * activation link. Shown once and never persisted client-side.
 * Reissuing invalidates any prior PIN — the confirmation copy
 * makes that explicit (§8).
 *
 * The dialog composes the activation URL locally from the
 * `company_id` / `employee_id` / `activation_code` returned by
 * `POST /api/workforce/employees/{id}/mobile-activation`. It
 * never displays raw UUIDs — the identifiers live inside the
 * link only (§22).
 */
import { useCallback, useState } from "react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export default function EmployeeMobileAccessDialog({
    open, onOpenChange, employee, restaurantId,
}) {
    // Prompt 20B §5 · §6 · §17 — surface the EXTRA browser-first
    // context inline. DIRECT / legacy rows keep the historical
    // wording exactly (installable Employee App is normal); EXTRA
    // adds an explicit "Browser access" section so the manager
    // knows the copyable link is meant to be opened directly in
    // the worker's phone browser without installing anything.
    const isExtra = (employee?.worker_type || "DIRECT") === "EXTRA";
    const [issuing, setIssuing] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [issued, setIssued] = useState(null);

    const _close = useCallback(() => {
        // Plaintext code is discarded on close (§8).
        setIssuing(false);
        setConfirmed(false);
        setIssued(null);
        onOpenChange(false);
    }, [onOpenChange]);

    const _issue = useCallback(async () => {
        if (!employee?.employee_id || !restaurantId || issuing) return;
        setIssuing(true);
        try {
            const { data } = await api.post(
                `/workforce/employees/${employee.employee_id}`
                + "/mobile-activation",
                { restaurant_id: restaurantId },
            );
            setIssued({
                company_id: data.company_id,
                employee_id: data.employee_id,
                activation_code: data.activation_code,
                expires_at: data.expires_at,
                // Prompt 15B-GATE §1 — the activation link MUST NOT
                // carry the plaintext activation code. It carries
                // only the non-secret device-scoping identifiers
                // (`c` = company_id, `e` = employee_id) required
                // by the committed 15A API. The employee types the
                // one-time activation code separately from paper /
                // chat / SMS — never from URL history / Referer.
                link: (
                    `${window.location.origin}/employee/workforce`
                    + `?c=${encodeURIComponent(data.company_id)}`
                    + `&e=${encodeURIComponent(data.employee_id)}`
                ),
            });
        } catch (err) {
            const status = err?.response?.status;
            if (status === 404) toast.error(i18n.t("wf.employeeMobileAccessDialog.employeeNotFound", "Employee not found."));
            else if (status === 403) toast.error(
                i18n.t("wf.employeeMobileAccessDialog.youCannotIssueMobileAccessFor", "You cannot issue mobile access for this employee."));
            else toast.error(i18n.t("wf.employeeMobileAccessDialog.couldNotCreateActivationCodeTry", "Could not create activation code. Try again."));
        } finally { setIssuing(false); }
    }, [employee, restaurantId, issuing]);

    const _copy = useCallback(async (value) => {
        try {
            await navigator.clipboard.writeText(value);
            toast.success(i18n.t("wf.employeeMobileAccessDialog.copiedToClipboard", "Copied to clipboard"));
        } catch { toast.error(i18n.t("wf.employeeMobileAccessDialog.couldNotCopy", "Could not copy.")); }
    }, []);

    return (
        <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(v) : _close())}>
            <DialogContent
                data-testid="employee-mobile-access-dialog"
                className="max-w-md"
            >
                <DialogHeader>
                    <DialogTitle
                        data-testid="employee-mobile-access-dialog-title"
                    >
                        Mobile access · {employee?.first_name}{" "}
                        {employee?.last_name}
                    </DialogTitle>
                </DialogHeader>
                {!issued && (
                    <div className="space-y-3">
                        {!confirmed ? (
                            <>
                                <p className="text-sm text-neutral-700">
                                    {i18n.t("wf.employeeMobileAccessDialog.createANewEmployeeActivationCode", "Create a new employee activation code?")}
                                </p>
                                <p className="text-sm text-neutral-500">
                                    {i18n.t("wf.employeeMobileAccessDialog.theEmployeeWillNeedToActivate", "The employee will need to activate their mobile access again.")}
                                </p>
                                <DialogFooter>
                                    <Button
                                        variant="outline"
                                        onClick={_close}
                                        data-testid="employee-mobile-access-cancel"
                                    >
                                        {i18n.t("common.cancel", "Cancel")}
                                    </Button>
                                    <Button
                                        onClick={() => setConfirmed(true)}
                                        data-testid="employee-mobile-access-confirm"
                                    >
                                        {i18n.t("wf.employeeMobileAccessDialog.continue", "Continue")}
                                    </Button>
                                </DialogFooter>
                            </>
                        ) : (
                            <>
                                <p className="text-sm text-neutral-700">
                                    {i18n.t("wf.employeeMobileAccessDialog.generateAOneTimeActivationCode", "Generate a one-time activation code and link for this employee.")}
                                </p>
                                <DialogFooter>
                                    <Button
                                        variant="outline"
                                        onClick={_close}
                                        data-testid="employee-mobile-access-cancel-2"
                                    >
                                        {i18n.t("common.cancel", "Cancel")}
                                    </Button>
                                    <Button
                                        onClick={_issue}
                                        disabled={issuing}
                                        data-testid="employee-mobile-access-issue"
                                    >
                                        {i18n.t("wf.employeeMobileAccessDialog.createActivationCode", "Create activation code")}
                                    </Button>
                                </DialogFooter>
                            </>
                        )}
                    </div>
                )}
                {issued && (
                    <div
                        data-testid="employee-mobile-access-issued"
                        className="space-y-3"
                    >
                        {isExtra && (
                            <div
                                data-testid="employee-mobile-access-extra-hint"
                                className="rounded-md border
                                            border-neutral-200
                                            bg-neutral-50 px-3 py-2
                                            text-xs text-neutral-700"
                            >
                                <div className="font-medium
                                    text-neutral-900">
                                    {i18n.t("wf.employeeMobileAccessDialog.browserAccess", "Browser access")}
                                </div>
                                <div>
                                    {i18n.t("wf.employeeMobileAccessDialog.sendThisLinkToTheExtra", "Send this link to the extra worker. They can open it directly in their phone browser — no app install required.")}
                                </div>
                            </div>
                        )}
                        <div className="space-y-1">
                            <p className="text-xs text-neutral-500">
                                {i18n.t("wf.employeeMobileAccessDialog.activationCode", "Activation code")}
                            </p>
                            <p
                                data-testid="employee-mobile-access-code"
                                className="font-mono text-lg text-neutral-900"
                            >
                                {issued.activation_code}
                            </p>
                        </div>
                        <div className="space-y-1">
                            <p className="text-xs text-neutral-500">
                                {i18n.t("wf.employeeMobileAccessDialog.activationLink", "Activation link")}
                            </p>
                            <p
                                data-testid="employee-mobile-access-link"
                                className="text-xs text-neutral-700
                                            break-all bg-neutral-50
                                            border border-neutral-200
                                            rounded px-2 py-2"
                            >
                                {issued.link}
                            </p>
                        </div>
                        <p className="text-xs text-neutral-500">
                            {i18n.t("wf.employeeMobileAccessDialog.expires24HoursThisCodeIs", "Expires: 24 hours. This code is shown once. Closing the dialog loses the plaintext code.")}
                        </p>
                        <DialogFooter className="flex flex-col gap-2">
                            <Button
                                variant="outline"
                                data-testid="employee-mobile-access-copy-code"
                                onClick={() => _copy(issued.activation_code)}
                            >
                                {i18n.t("wf.employeeMobileAccessDialog.copyCode", "Copy code")}
                            </Button>
                            <Button
                                variant="outline"
                                data-testid="employee-mobile-access-copy-link"
                                onClick={() => _copy(issued.link)}
                            >
                                {i18n.t("wf.employeeMobileAccessDialog.copyLink", "Copy link")}
                            </Button>
                            <Button
                                data-testid="employee-mobile-access-done"
                                onClick={_close}
                            >
                                {i18n.t("wf.employeeMobileAccessDialog.done", "Done")}
                            </Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
